import { Link, useLocation } from 'react-router-dom'
import { routes } from '@/router/routes'
import type { RouteWithMeta } from '@/router/types'

interface BreadcrumbItem {
  label: string
  path?: string  // path가 없으면 링크가 아닌 텍스트로 표시
}

// routes에서 AdminLayout의 children 추출
const getAdminRoutes = (): RouteWithMeta[] => {
  const rootRoute = routes.find(r => r.path === '/')
  if (!rootRoute?.children) return []

  const adminLayoutRoute = rootRoute.children.find(r => r.path === '')
  if (!adminLayoutRoute?.children) return []

  return adminLayoutRoute.children
}

// 동적 파라미터를 고려한 경로 매칭
const matchPath = (routePath: string, targetPath: string): boolean => {
  const routeParts = routePath.split('/').filter(Boolean)
  const targetParts = targetPath.split('/').filter(Boolean)

  if (routeParts.length !== targetParts.length) {
    return false
  }

  for (let i = 0; i < routeParts.length; i++) {
    // :id 같은 동적 파라미터는 어떤 값이든 매칭
    if (routeParts[i].startsWith(':')) {
      continue
    }
    // 정적 부분은 정확히 일치해야 함
    if (routeParts[i] !== targetParts[i]) {
      return false
    }
  }

  return true
}

// 재귀적으로 라우트 찾기
const findRouteByPath = (routeList: RouteWithMeta[], targetPath: string, parentPath = ''): RouteWithMeta | null => {
  for (const route of routeList) {
    // 현재 라우트의 전체 경로 계산
    const currentPath = route.path?.startsWith('/')
      ? route.path
      : `${parentPath}/${route.path || ''}`.replace(/\/+/g, '/').replace(/\/$/, '') || '/'

    // 동적 파라미터를 고려한 경로 매칭
    if (matchPath(currentPath, targetPath)) {
      return route
    }

    // children이 있으면 재귀적으로 탐색
    if (route.children && route.children.length > 0) {
      const found = findRouteByPath(route.children, targetPath, currentPath)
      if (found) {
        return found
      }
    }
  }

  return null
}

// 경로의 breadcrumb 생성
const buildBreadcrumbItems = (pathname: string): BreadcrumbItem[] => {
  const items: BreadcrumbItem[] = []

  // 홈 추가
  items.push({
    label: '홈',
    path: '/'
  })

  // pathname을 분해해서 각 depth의 라우트 찾기
  const pathParts = pathname.split('/').filter(Boolean)
  const adminRoutes = getAdminRoutes()

  let currentPath = ''
  for (let i = 0; i < pathParts.length; i++) {
    currentPath += '/' + pathParts[i]

    const route = findRouteByPath(adminRoutes, currentPath)

    if (route?.meta?.label) {
      const isLast = i === pathParts.length - 1

      // element가 없고 children만 있는 라우트는 링크 제거 (클릭 불가)
      const hasElement = route.element !== undefined
      const hasOnlyChildren = !hasElement && route.children && route.children.length > 0

      items.push({
        label: route.meta.label,
        // 마지막 항목이거나, element가 없고 children만 있으면 링크 제거
        path: (isLast || hasOnlyChildren) ? undefined : currentPath
      })
    }
  }

  return items
}

export default function Breadcrumb() {
  const location = useLocation()
  const breadcrumbItems = buildBreadcrumbItems(location.pathname)

  // 홈만 있으면 breadcrumb 표시 안함
  if (breadcrumbItems.length <= 1) {
    return null
  }

  return (
    <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
      {breadcrumbItems.map((item, index) => (
        <div key={index} className="flex items-center space-x-2">
          {index > 0 && <span className="text-gray-400">&gt;</span>}
          {item.path ? (
            <Link
              to={item.path}
              className="hover:text-gray-900 transition-colors"
            >
              {item.label}
            </Link>
          ) : (
            <span className="font-semibold text-gray-900">
              {item.label}
            </span>
          )}
        </div>
      ))}
    </nav>
  )
}